import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Checkbox, Chip, IconButton, Typography } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';

interface CandidateCardProps {
  matchPercentage: number;
  visaStatus: string;
  source: string;
  name: string;
  availability: string;
  availabilityDate: string;
  email: string;
  phone: string;
  location: string;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onExpand?: () => void;
  topSkills?: string;
  isShortList: boolean;
  submittedOn?: string;
  submittedBy?: string;
  activeTab?: number;
}

const CandidateCard: React.FC<CandidateCardProps> = ({
  matchPercentage,
  visaStatus,
  source,
  name,
  availability,
  availabilityDate,
  email,
  phone,
  location,
  isSelected,
  onSelect,
  onExpand,
  topSkills = '',
  isShortList = false,
  submittedOn,
  submittedBy,
  activeTab,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getMatchColor = (percentage: number) => {
    // if (percentage >= 90) return '#10b981'; // Green
    if (percentage >= 80) return '#EBF0FF'; // Blue
    // if (percentage >= 70) return '#f59e0b'; // Orange
    // return '#6b7280'; // Gray
    return '#EBF0FF';
  };

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
    if (onExpand) {
      onExpand();
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#FAFAFA',
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        marginBottom: '12px',
        marginRight: '4px',
        position: 'relative',
      }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
        }}>
        <div style={{ padding: '16px', paddingBottom: '4px' }}>
          {/* Top Row - Checkbox, Match Percentage, Tags */}
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}>
            <div style={{ alignItems: 'center', display: 'flex', gap: '10px' }}>
              {/* Match Percentage Circle */}
              {matchPercentage && (
                <div
                  style={{
                    alignItems: 'center',
                    backgroundColor: getMatchColor(matchPercentage),
                    border: '1px solid #7995E8',
                    borderRadius: '9999px',
                    color: '#2453C3',
                    display: 'flex',
                    fontFamily: 'Red Hat Display semibold',
                    fontSize: '12px',
                    fontWeight: '600',
                    justifyContent: 'center',
                    minHeight: '22px',
                    minWidth: '40px',
                    padding: '4px',
                  }}>
                  {matchPercentage}%
                </div>
              )}
              {/* Tags */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {visaStatus && (
                  <Chip
                    label={visaStatus}
                    size="small"
                    sx={{
                      backgroundColor: '#F0F0F0',
                      border: '1px solid #DBDBDB',
                      color: '#7C7C7C',
                      fontFamily: 'Red Hat Display semibold',
                      fontSize: '12px',
                      fontWeight: '500',
                      height: '20px',
                      padding: '4px',
                    }}
                  />
                )}
                {source && (
                  <Chip
                    label={source}
                    size="small"
                    sx={{
                      backgroundColor: '#FFE3E3',
                      border: '1px solid #E87979',
                      color: '#C32424',
                      fontFamily: 'Red Hat Display semibold',
                      fontSize: '12px',
                      fontWeight: '500',
                      height: '20px',
                      padding: '4px',
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Candidate Name and Availability Row */}
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              gap: '12px',
              marginBottom: '8px',
            }}>
            {activeTab === 1 && (
              <Checkbox
                checked={isSelected}
                onChange={(e) => onSelect(e.target.checked)}
                sx={{
                  '&.Mui-checked': { color: '#616161' },
                  color: '#979797 !important',
                  padding: '4px',
                }}
              />
            )}
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* Candidate Name */}
                <Typography
                  sx={{
                    color: '#242424',
                    fontFamily: 'Red Hat Display bold',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}>
                  {name ? name : ''}
                </Typography>

                {/* Availability */}
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                  }}>
                  {availability && (
                    <>
                      <CheckCircleIcon
                        sx={{
                          color: '#0E700E',
                          fontFamily: 'Red Hat Display semibold',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginRight: '6px',
                        }}
                      />
                      <Typography
                        sx={{
                          color: '#0E700E',
                          fontFamily: 'Red Hat Display semibold',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginRight: '6px',
                        }}>
                        {availability}
                      </Typography>
                    </>
                  )}

                  {availabilityDate && (
                    <>
                      <span
                        style={{
                          backgroundColor: '#0E700E',
                          borderRadius: '100%',
                          height: '4px',
                          marginRight: '6px',
                          width: '4px',
                        }}
                      />
                      <Typography
                        sx={{
                          color: '#0E700E',
                          fontFamily: 'Red Hat Display semibold',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginRight: '6px',
                        }}>
                        {availabilityDate}
                      </Typography>
                    </>
                  )}
                </div>
              </div>
              {/* Contact Information */}
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: '8px',
                  // marginBottom: isExpanded ? '16px' : '0',
                }}>
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Red Hat Display Medium',
                    fontSize: '13px',
                    marginBottom: '2px',
                  }}>
                  {email ? email : '-'}
                </Typography>
                <span
                  style={{
                    backgroundColor: '#D9D9D9',
                    borderRadius: '100%',
                    height: '4px',
                    width: '4px',
                  }}
                />
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Red Hat Display Medium',
                    fontSize: '13px',
                    marginBottom: '2px',
                  }}>
                  {phone ? phone : '-'}
                </Typography>
                <span
                  style={{
                    backgroundColor: '#D9D9D9',
                    borderRadius: '100%',
                    height: '4px',
                    width: '4px',
                  }}
                />
                <Typography
                  sx={{
                    color: '#616161',
                    fontFamily: 'Red Hat Display Medium',
                    fontSize: '13px',
                  }}>
                  {location ? location : '-'}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        {/* Expand Arrow */}
        {isShortList ? (
          <Typography
            component={'div'}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '16px',
              padding: '16px 16px 4px',
            }}>
            <Typography
              component={'div'}
              sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography
                sx={{
                  color: '#616161',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '13px',
                }}>
                Submitted by
              </Typography>
              <Typography
                sx={{
                  color: '#242424',
                  fontFamily: 'Red Hat Display Medium',
                  fontSize: '14px',
                  fontWeight: '600',
                }}>
                {submittedBy}
              </Typography>
            </Typography>
            <Typography
              component={'div'}
              sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography
                sx={{
                  color: '#616161',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '13px',
                }}>
                Submitted on
              </Typography>
              <Typography
                sx={{
                  color: '#242424',
                  fontFamily: 'Red Hat Display Medium',
                  fontSize: '14px',
                  fontWeight: '600',
                }}>
                {submittedOn && moment(submittedOn).isValid()
                  ? moment(submittedOn).format('DD MMM YYYY')
                  : '-'}
              </Typography>
            </Typography>
          </Typography>
        ) : (
          <div
            style={{
              alignContent: 'center',
              borderLeft: '1px solid #e5e7eb',
              height: '-webkit-fill-available',
            }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleExpandClick();
              }}
              sx={{
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                borderLeft: '1px solid #e5e7eb',
                padding: '4px',
              }}>
              <ExpandMoreIcon
                sx={{
                  color: '#6b7280',
                  fontSize: '20px',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </IconButton>
          </div>
        )}
      </div>

      {/* Top Skills Section - Only show when expanded */}
      {!isShortList && isExpanded && (
        <div
          style={{
            borderTop: '1px solid #e5e7eb',
            padding: '16px',
          }}>
          <Typography
            sx={{
              color: '#242424',
              fontFamily: 'Red Hat Display Bold',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
            }}>
            Top Skills
          </Typography>
          <Typography
            sx={{
              color: '#616161',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '13px',
              lineHeight: '1.5',
            }}>
            {topSkills}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default CandidateCard;
